import React from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "../Icon";
import { useIsMobile } from "../primitives/useIsMobile";
import { useTimeTracking } from "../../hooks/useTimeTracking";
import { useShiftWarnings } from "../../hooks/useShiftWarnings";
import useCurrentMembership from "../../helpers/useCurrentMembership";

function pad(n) {
  return n < 10 ? `0${n}` : String(n);
}

const STATE_CONFIG = {
  working: {
    bg: "var(--kr-action)",
    fg: "#fff",
    label: "Trabajando",
    showTime: true,
    showDot: true,
  },
  working_warning: {
    bg: "var(--kr-warning-600)",
    fg: "#fff",
    label: "Próxima a finalizar",
    showTime: true,
    showDot: true,
  },
  working_limit: {
    bg: "var(--kr-danger-600)",
    fg: "#fff",
    label: "Jornada alcanzada",
    showTime: true,
    icon: "AlertTriangle",
  },
  paused: {
    bg: "var(--kr-warning-500)",
    fg: "#fff",
    label: "En pausa",
    showTime: true,
    showDot: true,
  },
  off: {
    bg: "var(--kr-bg-surface)",
    fg: "var(--kr-fg-strong)",
    label: "Marcar entrada",
    border: "var(--kr-border)",
    icon: "Play",
  },
  finished: {
    bg: "var(--kr-neutral-100)",
    fg: "var(--kr-fg-strong)",
    label: "Jornada finalizada",
    icon: "Check",
    showTime: true,
  },
};

export function ClockPill() {
  const { employeeProfileId, loading: membershipLoading } = useCurrentMembership();

  // Sin perfil de empleado en este workspace no hay nada que fichar
  // (owners/admins externos, p. ej. dueños del grupo). El backend respondería
  // 422 NO_EMPLOYEE_PROFILE; lo evitamos en el cliente.
  if (membershipLoading || !employeeProfileId) {
    return null;
  }

  return <ClockPillContent />;
}

function ClockPillContent() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { me } = useCurrentMembership();
  const { jornadaState, seconds, loading, clockIn } = useTimeTracking({
    poll: true,
    tick: true,
  });
  const { warning } = useShiftWarnings(me?.id || null);
  const [busy, setBusy] = React.useState(false);

  let effectiveState = jornadaState;
  if (jornadaState === "working" && warning) {
    effectiveState = warning.type === "limit_reached"
      ? "working_limit"
      : "working_warning";
  }
  const cfg = STATE_CONFIG[effectiveState];
  const hh = Math.floor(seconds / 3600);
  const mm = Math.floor((seconds % 3600) / 60);
  const timeText = `${pad(hh)}:${pad(mm)}`;

  const handle = async () => {
    if (busy) return;
    if (jornadaState === "off") {
      setBusy(true);
      try {
        await clockIn();
      } catch (_) {
        navigate("/jornada");
      } finally {
        setBusy(false);
      }
    } else {
      navigate("/jornada");
    }
  };

  const disabled = busy || loading;

  return (
    <button
      type="button"
      onClick={handle}
      disabled={disabled}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        height: 32,
        padding: "0 12px",
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 600,
        background: cfg.bg,
        color: cfg.fg,
        border: cfg.border ? `1px solid ${cfg.border}` : "1px solid transparent",
        cursor: disabled ? "wait" : "pointer",
        flexShrink: 0,
        transition: "background var(--kr-dur-fast) var(--kr-ease-out)",
      }}
    >
      {cfg.icon ? (
        <Icon name={cfg.icon} size={12} />
      ) : cfg.showDot ? (
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: 999,
            background: "#fff",
            boxShadow: "0 0 0 3px rgba(255,255,255,.3)",
          }}
        />
      ) : null}
      {/* En mobile escondemos el label textual cuando además mostramos
          el tiempo — el icono/dot + tiempo bastan para entender estado. */}
      {!(isMobile && cfg.showTime) && <span>{cfg.label}</span>}
      {cfg.showTime && (
        <span
          style={{
            fontFamily: "var(--kr-font-mono)",
            fontVariantNumeric: "tabular-nums",
            opacity: 0.9,
          }}
        >
          {isMobile ? timeText : `· ${timeText}`}
        </span>
      )}
    </button>
  );
}

export default ClockPill;
